import { createContext, useContext, ReactNode } from 'react'
import { useAuthStore } from '~/app/services/auth/auth.store'
import { Tenant } from '~/app/services/auth/auth.type'
import { TenantApi, TenantConfig } from '~/app/services/tenant'
import { useQuery } from '@tanstack/react-query'

interface TenantContextType {
  selectedTenant: Tenant | null
  tenantConfig: TenantConfig | null
  isLoadingConfig: boolean
}

const TenantContext = createContext<TenantContextType | undefined>(undefined)

export const useTenant = () => {
  const context = useContext(TenantContext)
  if (!context) {
    throw new Error('useTenant must be used within TenantProvider')
  }
  return context
}

interface TenantProviderProps {
  children: ReactNode
}

export const TenantProvider = ({ children }: TenantProviderProps) => {
  const { selectedTenant, accessToken } = useAuthStore()

  const { data: tenantConfig, isLoading: isLoadingConfig } = useQuery({
    queryKey: ['tenant-config', selectedTenant?.id],
    queryFn: TenantApi.getTenantConfig,
    enabled: !!selectedTenant && !!accessToken,
  })

  return (
    <TenantContext.Provider
      value={{
        selectedTenant,
        tenantConfig: tenantConfig || null,
        isLoadingConfig,
      }}
    >
      {children}
    </TenantContext.Provider>
  )
}
